import React from 'react';
import { FiAlertCircle, FiCheckCircle, FiDatabase, FiHelpCircle, FiInfo, FiXCircle } from 'react-icons/fi';
import { getEddyStateNotice, getEddyUiCopy } from './eddyBetaUi';

const noticeIcons = {
  ambiguous: FiHelpCircle,
  clarify: FiHelpCircle,
  unknown: FiAlertCircle,
  unavailable: FiInfo,
  unsupported: FiInfo,
  scope: FiInfo,
};

function StateNotice({ notice }) {
  if (!notice) return null;
  const Icon = noticeIcons[notice.type] || FiInfo;
  return (
    <div className="mt-4 flex gap-3 rounded-2xl border border-edge bg-surface-muted/60 p-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-edge bg-surface text-link"><Icon aria-hidden /></span>
      <div><p className="text-xs font-semibold text-ink">{notice.title}</p><p className="mt-1 text-[11px] leading-relaxed text-ink-muted">{notice.body}</p></div>
    </div>
  );
}

function RequirementAssessmentCard({ assessment, copy }) {
  if (!assessment) return null;
  const rowPresentation = {
    met: { Icon: FiCheckCircle, label: copy.requirementMet, tone: 'text-success' },
    'not-met': { Icon: FiXCircle, label: copy.requirementNotMet, tone: 'text-danger' },
    unknown: { Icon: FiHelpCircle, label: copy.unableToDetermine, tone: 'text-ink-muted' },
  };
  const status = assessment.status || assessment.outcome;
  const overallTone = status === 'not_eligible' || status === 'not-eligible' ? 'text-danger' : status === 'unable_to_determine' || status === 'unknown' ? 'text-ink-muted' : 'text-success';
  const overall = assessment.overall || (
    status === 'eligible'
      ? copy.requirementMet
      : status === 'not_eligible' || status === 'not-eligible'
        ? copy.requirementNotMet
        : copy.unableToDetermine
  );
  const detailRows = [
    ['Student band', assessment.details?.studentBand],
    ['Required band', assessment.details?.requiredBand],
    ['Ranking position', assessment.details?.rankingPosition]
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');
  return (
    <section className="mt-4 overflow-hidden rounded-2xl border border-edge bg-surface-muted/45">
      <div className="border-b border-edge bg-surface px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-link">{copy.requirementAssessment}</p>
        <div className="mt-1 flex items-center justify-between gap-3"><h3 className="text-xs font-semibold text-ink">{assessment.programmeName || assessment.programmeCode}</h3><span className="rounded-full border border-edge bg-surface-muted px-2 py-0.5 text-[9px] font-semibold text-ink-subtle">{assessment.code || assessment.programmeCode}</span></div>
      </div>
      {assessment.rows?.length ? <div>
        {assessment.rows.map((row) => {
          const presentation = rowPresentation[row.status] || rowPresentation.unknown;
          const RowIcon = presentation.Icon;
          return <div key={row.subject} className="grid grid-cols-[1fr_auto] gap-3 border-b border-edge px-4 py-3 last:border-b-0"><div className="flex items-start gap-2"><RowIcon className={`mt-0.5 shrink-0 ${presentation.tone}`} aria-hidden /><div><p className="text-[11px] font-semibold text-ink">{row.subject}</p><p className="mt-0.5 text-[10px] text-ink-subtle">{copy.yourScore}: {row.yourScore}</p></div></div><div className="text-right"><p className={`text-[10px] font-semibold ${presentation.tone}`}>{presentation.label}</p><p className="mt-0.5 text-[10px] text-ink-subtle">{copy.required}: {row.required}</p></div></div>;
        })}
      </div> : null}
      {detailRows.length ? <div className="grid gap-px bg-edge sm:grid-cols-3">{detailRows.map(([label, value]) => <div key={label} className="bg-surface px-4 py-3"><p className="text-[9px] font-semibold uppercase tracking-wider text-ink-subtle">{label}</p><p className="mt-1 text-sm font-semibold text-ink">{String(value)}</p></div>)}</div> : null}
      <div className="border-t border-edge bg-surface px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">{copy.overall}</p><p className={`mt-1 text-xs font-semibold ${overallTone}`}>{overall}</p>{assessment.note || assessment.requReason ? <p className="mt-2 text-[10px] leading-relaxed text-ink-subtle">{assessment.note || assessment.requReason}</p> : null}</div>
    </section>
  );
}

function ScoreRiskAssessmentCard({ assessment, copy }) {
  if (!assessment) return null;
  const metrics = [
    [copy.calculatedScore, assessment.calculatedScore ?? assessment.userScore ?? '—'],
    [copy.storedReference, assessment.storedReference ?? assessment.reference?.median ?? '—'],
    [copy.classification, assessment.classificationLabel ?? assessment.classification ?? '—'],
  ];
  return (
    <section className="mt-4 overflow-hidden rounded-2xl border border-edge bg-surface-muted/45">
      <div className="border-b border-edge bg-surface px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-link">{copy.scoreRiskAssessment}</p>
        <div className="mt-1 flex items-center justify-between gap-3"><h3 className="text-xs font-semibold text-ink">{assessment.programmeName || assessment.programmeCode}</h3><span className="rounded-full border border-edge bg-surface-muted px-2 py-0.5 text-[9px] font-semibold text-ink-subtle">{assessment.code || assessment.programmeCode}</span></div>
      </div>
      <div className="grid gap-px bg-edge sm:grid-cols-3">
        {metrics.map(([label, value]) => <div key={label} className="bg-surface px-4 py-4"><p className="text-[9px] font-semibold uppercase tracking-wider text-ink-subtle">{label}</p><p className="mt-2 text-xl font-semibold text-ink">{value}</p></div>)}
      </div>
      <div className="border-t border-edge px-4 py-3"><p className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-muted"><FiDatabase className="text-link" aria-hidden />{copy.referenceYear} · {assessment.referenceYear || assessment.admissionYear || '—'}</p><p className="mt-2 text-[10px] leading-relaxed text-ink-subtle">{assessment.note || copy.referenceBoundary}</p></div>
    </section>
  );
}

function ChatAssessmentCards({ requirementAssessment, scoreRiskAssessment, stateNotice, language = 'en' }) {
  const copy = getEddyUiCopy(language);
  const resolvedNotice = stateNotice?.code
    ? getEddyStateNotice(stateNotice.code, language)
    : stateNotice;
  return (
    <>
      <RequirementAssessmentCard assessment={requirementAssessment} copy={copy} />
      <ScoreRiskAssessmentCard assessment={scoreRiskAssessment} copy={copy} />
      <StateNotice notice={resolvedNotice} />
    </>
  );
}

export default ChatAssessmentCards;
